import TimeRange from "@/types/time-range";
import {
  PublishedDashboard,
  EdrObservationsMapLayer as EdrObservationsLayerConfig,
} from "@/lib/api";
import Layer from "@/lib/map-layers/layer";
import { DimensionValueSet } from "@/lib/dimensions";
import {
  dynamicDimensionsContainValues,
  getRelevantDimensionValues,
} from "@/lib/dimensions/helpers";
import { TimeRangeDimensionValue } from "@/lib/dimensions/time-range-dimension";
import { EnumLayerSetting } from "@/lib/map-layers/layer-settings/enum-layer-setting";
import { LayerSettingValueSet } from "@/lib/map-layers/layer-settings";
import LegendDimensions from "@/features/shared/widgets/map/legends/legend-dimensions";

import EdrObservationsLayerDataLoader, {
  EdrObservationsLayerData,
} from "./edr-observations-layer-data-loader";
import EdrObservationsMarkers from "./edr-observations-markers";

// TODO: use EdrObservationsMapLayerAllOf1ParameterSets
export type EdrParameterSet = {
  title: string;
  parameters: string[];
};

export default class EdrOberservationsLayer implements Layer {
  private layerDataLoader: EdrObservationsLayerDataLoader;

  private config;
  private data: EdrObservationsLayerData | undefined;

  constructor(config: EdrObservationsLayerConfig, selectedDashboard: PublishedDashboard) {
    this.config = config;
    this.layerDataLoader = new EdrObservationsLayerDataLoader(
      selectedDashboard.id,
      config.id,
    );
  }

  get id() {
    return this.config.id;
  }
  getTitle() {
    return this.config.title;
  }
  get dimensions() {
    return this.data?.dimensions;
  }

  get settings() {
    if (this.config.parameter_sets.length <= 0) return [];

    const parameterSetSettingValues = this.config.parameter_sets.map(
      (parameterSet, index) => {
        return {
          key: `edr_parameter_set_${index}`,
          label: parameterSet.title,
          value: parameterSet,
        };
      },
    );

    return [
      new EnumLayerSetting<EdrParameterSet>({
        id: "parameter_set",
        values: parameterSetSettingValues,
        default: parameterSetSettingValues[0],
      }),
    ];
  }

  isForecast() {
    return false;
  }
  async preload() {
    return;
  }

  renderLegend(dimensionValues: DimensionValueSet) {
    return <LegendDimensions dimensionValues={dimensionValues} />;
  }

  renderLayer() {
    return undefined;
  }

  getRelevantDimensionValues(values: DimensionValueSet): DimensionValueSet {
    return getRelevantDimensionValues(this.dimensions, values);
  }

  async initialize(timeRange: TimeRange) {
    try {
      this.data = await this.layerDataLoader.loadLayerData(timeRange);
    } catch (error) {
      this.data = undefined;
      throw error;
    }
  }

  isLoaded() {
    return this.data !== undefined;
  }

  hasLoadError(_dimensionValues: DimensionValueSet) {
    return false;
  }

  hasPartialLoadErrors() {
    return false;
  }

  clearLoadFailures() {
    // no-op
  }

  isActive(dimensionValues: DimensionValueSet) {
    if (!this.data) return false;
    return dynamicDimensionsContainValues(this.data.dimensions, dimensionValues);
  }

  renderComponents(
    currentDimensionValues: DimensionValueSet,
    settingValues: LayerSettingValueSet,
  ) {
    const relevantCurrentDimensionValues =
      this.getRelevantDimensionValues(currentDimensionValues);

    if (!this.data) return undefined;
    if (!this.isActive(currentDimensionValues)) return undefined;

    return (
      <EdrObservationsMarkers
        locations={this.data.locations}
        layer={this.config}
        currentTime={relevantCurrentDimensionValues["time"] as TimeRangeDimensionValue}
        currentSettingValues={settingValues}
      />
    );
  }
}
